import BodyText from 'LuxKit/Typography/BodyText'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import useCopyWithFeedback from 'hooks/useCopyWithFeedback'
import { formatEsimPromoCodeDataBadgeLabel } from 'loyalty/utils/loyaltyEsimLabels'
import { tierNormalBadgeStyle } from 'styles/utils/tierStyles'

const CopiedFeedback = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  line-height: 1;
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(8)};
  padding: ${rem(16)};
  flex: 1 1 ${rem(240)};
  min-width: 0;
  min-height: 0;
`

const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`

const CardHeaderLeft = styled(Group)`
  flex: 1;
  min-width: 0;
`

const ViewOrderLink = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${rem(2)};
  color: ${props => props.theme.palette.brand.primary.normal};
  font-size: ${rem(12)};
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`

const PromoCodeItem = styled(ClickableListItem)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

/** Matches no-code variation message: grey background (neutral.seven), no border-radius. */
const AvailableLaterBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const AvailableLaterIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const GeneratingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(8)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(6)};
`

const CardTitle = styled(BodyText)`
  min-width: 0;
`

const DataBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: ${rem(1)};
  padding: ${rem(2)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.S};
  font-size: ${rem(11)};
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  ${tierNormalBadgeStyle}
`

interface Props {
  destination: string | null
  dates: string
  phase: App.LoyaltyEsimCodePhase
  code?: string
  availableDate?: string
  orderId?: string
  tier?: App.LoyaltyTier
  dataAllowanceGb?: number
  unlimitedDays?: number
}

function LoyaltyEsimDetailsPromoCodeCard(props: Props) {
  const { destination, dates, phase, code, availableDate, orderId, tier, dataAllowanceGb, unlimitedDays } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { resolve } = useModalElementContext()
  const { isCopied: showCopiedTooltip, copyText: copyToClipboard } = useCopyWithFeedback()

  const handleCopyClick = useCallback(() => {
    if (!code) return
    copyToClipboard(code)
  }, [code, copyToClipboard])

  const handleViewOrder = useCallback(() => {
    if (!orderId) return
    resolve()
    dispatch(pushWithRegion(`/account/my-escapes/order/${orderId}`))
  }, [dispatch, orderId, resolve])

  const dataBadgeLabel = formatEsimPromoCodeDataBadgeLabel(intl, { dataAllowanceGb, unlimitedDays })

  const destinationLabel = destination || intl.formatMessage({
    id: '-GjamtyKuhCBKmPIdQdE',
    defaultMessage: 'International eSIM',
    description: 'Fallback destination label for eligible eSIM orders when specific destination names are unavailable',
  })

  const copiedFeedbackIcon = (
    <CopiedFeedback>
      <BodyText variant="small" colour="light-foreground-alt">
        {intl.formatMessage({
          id: '_yk6U9B2jYDeO5WBmcSv',
          defaultMessage: 'Copied',
          description: 'Text shown beside tick icon after copying eSIM promo code',
        })}
      </BodyText>
      <LineCheckIcon size="S" />
    </CopiedFeedback>
  )

  return (
    <CardContainer>
      <CardHeaderRow>
        <CardHeaderLeft direction="vertical" gap={4}>
          <Group direction="horizontal" gap="2 8" wrap="wrap" fullWidth>
            <CardTitle variant="medium" weight="bold">{destinationLabel}</CardTitle>
            {tier && dataBadgeLabel && <DataBadge className={`tier-${tier}`}>
              {dataBadgeLabel}
            </DataBadge>}
          </Group>
          <BodyText variant="medium">{dates}</BodyText>
        </CardHeaderLeft>
        {orderId && <ViewOrderLink onClick={handleViewOrder}>
          {intl.formatMessage({
            id: 'NQrocXzL8j1WbaN9B8Rx',
            defaultMessage: 'View order',
            description: 'Link text on eSIM promo card to open this booking in My Escapes',
          })}
          <LineAngleRightIcon size="XS" />
        </ViewOrderLink>}
      </CardHeaderRow>

      {phase === 'loading' && <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />}

      {phase === 'generating' && <GeneratingBox>
        <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />
        <BodyText variant="small" colour="light-foreground-alt">
          {intl.formatMessage({
            id: 'VwWj-NPVDEGK-lTnHgwU',
            defaultMessage: 'Generating your code, this may take up to 5 minutes…',
            description: 'Message shown while eSIM promo code is being generated',
          })}
        </BodyText>
      </GeneratingBox>}

      {phase === 'success' && !!code && <PromoCodeItem
        title={code}
        titleLineClamp={1}
        size="medium"
        endIcon={showCopiedTooltip ? copiedFeedbackIcon : <LineCopyIcon size="S" />}
        onClick={handleCopyClick}
      />}

      {phase === 'available_later' && !!availableDate && <AvailableLaterBox>
        <AvailableLaterIcon>
          <LineInfoCircleIcon size="S" />
        </AvailableLaterIcon>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'CiVosmaCVp3MuhwRlIPH',
            defaultMessage: 'Code available on {date}',
            description: 'Message shown when eSIM promo code is not yet available',
          }, { date: availableDate })}
        </BodyText>
      </AvailableLaterBox>}

      {phase === 'error' && <MessageBanner
        kind="critical"
        icon={<LineInfoCircleIcon />}
      >
        {intl.formatMessage({
          id: 'oKIty-ovzL3IH40vIXVP',
          defaultMessage: 'Unable to load promo code. Please try again later.',
          description: 'Error message displayed when the eSIM promo code fails to load',
        })}
      </MessageBanner>}
    </CardContainer>
  )
}

export default LoyaltyEsimDetailsPromoCodeCard
